import { html } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { RgTransition } from '@src/js/common/rg-transition.js'

@customElement('rg-expand-transition')
export class RgExpandTransition extends RgTransition {

  @property({ type: String, attribute: 'height' })
  height = 'auto' // 'auto' | '100px' | '40%' | '50vh'

  @property({ type: Boolean, attribute: 'start-expanded' })
  startExpanded = false

  firstRender = true

  registerEvents() {
    return {
      'enter-start': this.onEnterStart,
      'enter-end': this.onEnterEnd,
      'leave-end': this.onEnterEnd,
      'leave-start': this.computeHeight,
    }
  }

  /**
   * Calculates the height of the element based on its contents.
   * By default, the height is set to 'auto', which means the element
   * will expand to fit its contents. If the height is set to a fixed
   * value the element will be stuck in that height.
   */
  getComputedHeight() {
    if (this.height !== 'auto') {
      return this.height
    }
    this.style.height = 'auto'
    const { width } = global.getComputedStyle(this)
    Object.assign(this.style, {
      width,
      position: 'absolute',
      visibility: 'hidden',
      height: 'auto',
    })
    const { height: result } = global.getComputedStyle(this)
    Object.assign(this.style, {
      width: null,
      position: null,
      visibility: null,
      height: null,
    })
    return result
  }

  computeHeight() {
    this.style.setProperty('--computed-height', this.getComputedHeight())
  }

  /**
   * Make sure we don't lock the height to fixed pixels after the transition.
   * This is necessary to handle dynamic height changes when this container expand,
   * like when content reflow makes the height bigger when set to auto.
   */
  onEnterEnd() {
    this.style.setProperty('--computed-height', this.height)
    if (this.firstRender && this.startExpanded) {
      this.firstRender = false;
      this.style.transitionProperty = '';
    }
  }

  connectedCallback() {
    super.connectedCallback()
    if (this.firstRender && this.startExpanded) {
      this.style.transitionProperty = 'none';
      this.active = true
      this.enterTransition()
    }
  }

  onEnterStart() {
    this.computeHeight()
  }

  render() {
    const scopedClass = `rg-expand-transition[${this.scopeId}]`
    return html`
      <style>
        ${scopedClass} {
          position: relative;
          backface-visibility: hidden;
          will-change: height;
          display: block;
          overflow: hidden;
          height: 0;
        }

        ${scopedClass}.enter-active,
        ${scopedClass}.leave-active {
          transition-property: height;
          transition-duration: 0.4s;
          transition-timing-function: ease-out;
        }

        ${scopedClass}.enter-active,
        ${scopedClass}.enter-to,
        ${scopedClass}.leave-from {
          height: var(--computed-height, 0);
        }

        ${scopedClass}.leave-active {
          height: 0;
        }

      </style>
      ${this.slot()}
    `
  }
}